import { faXmarkLarge } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Divider, Fade, Paper } from "@mui/material";
import React, { useCallback, useMemo, useState } from "react";
import { useToggle } from "react-use";

import DefaultModal from "components/shared/Modals/DefaultModal";
import { Typography } from "components/shared/Typography";

import { ModalProvider } from ".";
import useStyles from "./styles";
import type { ModalContainerProps, ModalContextPayload, ModalState, ShowModalOptions } from "./types";

function ModalContainer<T = Record<string, unknown>>({ children }: ModalContainerProps<T>): React.ReactElement {
  const defaultState = useMemo<ModalState<T>>(
    () => ({
      title: "",
      onClose: undefined,
      component: undefined,
      headerIcon: undefined,
      isCloseable: true,
      showDefaultHeader: true,
      showDivider: false,
      componentProps: {},
    }),
    [],
  );

  const [newComponentProps, setNewComponentProps] = useState<T | Record<string, unknown>>({});
  const [modalState, setModalState] = useState<ModalState<T>>(defaultState);
  const [on, toggle] = useToggle(false);

  const showModal = useCallback(
    (options: ShowModalOptions<T> | unknown) => {
      const modalOptions = options as ShowModalOptions<T>;

      const newState = { ...defaultState, ...modalOptions };

      setModalState(newState);
      toggle(true);
    },
    [defaultState, toggle],
  );

  const hideModal = useCallback(() => {
    setModalState(defaultState);
    toggle(false);
    modalState?.onClose?.();
  }, [defaultState, toggle, modalState]);

  const updateComponentProps = useCallback((props: Record<string, unknown>) => {
    const componentProps = props ?? {};
    setNewComponentProps(componentProps);
  }, []);

  const payload = useMemo<ModalContextPayload<T>>(
    () => [showModal, hideModal, updateComponentProps, { isOpen: on }],
    [showModal, hideModal, updateComponentProps, on],
  );

  const Component = modalState?.component;
  const HeaderIcon = modalState?.headerIcon;
  const componentProps = (modalState?.componentProps || {}) as T;

  const { classes } = useStyles({ minWidth: modalState.minWidth });

  return (
    <ModalProvider value={payload}>
      {children}

      <Fade in={on}>
        <div>
          <DefaultModal
            onClose={hideModal}
            open={on}
            BackdropProps={{ sx: modalState.isCloseable ? {} : { pointerEvents: "none" } }}
            disableAutoFocus
            style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
          >
            <Paper
              elevation={4}
              className={classes.paper}
            >
              {modalState.showDefaultHeader && (
                <>
                  <Box className={classes.title}>
                    <Box style={{ display: "flex", alignItems: "center" }}>
                      {HeaderIcon && (
                        <span style={{ color: modalState.headerIconColor || "inherit" }}>
                          <FontAwesomeIcon
                            icon={HeaderIcon}
                            size="lg"
                          />
                        </span>
                      )}

                      <Typography
                        variant="h4"
                        lineHeight="36px"
                        sx={{ marginLeft: HeaderIcon ? 1 : 0 }}
                      >
                        {modalState.title}
                      </Typography>
                    </Box>
                    {modalState.isCloseable && (
                      <FontAwesomeIcon
                        icon={faXmarkLarge}
                        style={{ cursor: "pointer" }}
                        onClick={() => toggle(false)}
                      />
                    )}
                  </Box>
                  {modalState.showDivider && <Divider />}
                </>
              )}

              <Box px={0}>
                {Component && (
                  <Component
                    componentProps={{ ...componentProps, ...newComponentProps }}
                    hideModal={hideModal}
                  />
                )}
              </Box>
            </Paper>
          </DefaultModal>
        </div>
      </Fade>
    </ModalProvider>
  );
}

export default ModalContainer;
